import { Component, OnInit, OnDestroy, ElementRef, HostListener, ViewChild, Input } from '@angular/core';
import { TasksByPracticeAreaService } from '../../services/tasks-by-practice-area.service';
import { ChartData, PracticeArea } from './tasks-by-practice-area.models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-tasks-by-practice-area',
    templateUrl: './tasks-by-practice-area.component.html',
    styleUrls: ['./tasks-by-practice-area.component.scss'],
    standalone: false
})
export class TasksByPracticeAreaComponent implements OnInit, OnDestroy {
    @ViewChild('chartContainer') chartContainer!: ElementRef;

    // Input property to control filter visibility from parent component
    @Input() isFilterVisible: boolean = false;
    
    // Fixed color mapping for practice areas
    private readonly practiceAreaColors: { [key: string]: string } = {
        'Bankruptcy': '#79BE73',        // Green
        'Corporate Services': '#5B7CE5', // Blue
        'Disbursements': '#E85B5B',     // Red
        'Director Services': '#F4C145',  // Yellow
        'Family Law': '#9D67F8',        // Purple
        'Finance Services': '#45C7F4',   // Light Blue
        'Fund Services': '#FF9F40',      // Orange
        'Immigration Services': '#4ECBB1', // Teal
        'Intellectual Property': '#6C757D', // Gray
        'International Services': '#FF6B8A', // Pink
        'KYC/AML': '#58C184',           // Forest Green
        'Labor & Employment': '#8E44AD', // Deep Purple
        'Legal Project Services': '#E67E22', // Dark Orange
        'Legal Research': '#16A085',     // Sea Green
        'Litigation': '#E74C3C',         // Bright Red
        'Treasury Services': '#7FB3D5',  // Light Blue
        'Trusts & Estates': '#C39BD3'    // Lavender
    };

    chartConfig = {
        view: undefined as [number, number] | undefined,
        scheme: {
            domain: [] as string[]
        },
        showXAxis: true,
        showYAxis: true,
        gradient: false,
        showLegend: false, // Disable legend
        legendPosition: 'none', // No legend position needed
        legendTitle: '', // No legend title needed
        maxLegendItems: 0, // No legend items
        roundDomains: true, // More similar to ngx-charts demo
        showGridLines: true, // Show grid lines
        barPadding: 8, // Padding between bars (same as the demo)
        trimXAxisTicks: true, // Trim ticks if they're too long
        trimYAxisTicks: true, // Trim ticks if they're too long
        maxXAxisTickLength: 16, // Max tick length
        maxYAxisTickLength: 16, // Max tick length
        legendOptions: {
            scaleType: 'ordinal',
            domain: [] as string[],
            colors: this.practiceAreaColors,
            position: 'right', // Position at right
            format: {
                text: (label: string) => label, // Don't truncate names
            }
        },
        showXAxisLabel: true,
        showYAxisLabel: false,
        xAxisLabel: 'Task Count',
        yAxisLabel: '',
        data: [] as ChartData[],
        filteredData: [] as ChartData[],
        selectedPracticeAreas: [] as string[]
    };

    loading = false;
    error: string | null = null;
    practiceAreas: PracticeArea[] = [];
    selectedPracticeAreas: string[] = [];
    // Filter visibility - always true as it's controlled by parent through isFilterVisible
    showFilters: boolean = true;
    selectedItems: Set<string> = new Set(); // Track selected items in legend
    private destroy$ = new Subject<void>();

    constructor(
        private tasksByPracticeAreaService: TasksByPracticeAreaService,
        private elementRef: ElementRef
    ) { }

    ngOnInit(): void {
        this.loadPracticeAreas();
        this.loadData();
        this.updateChartSize();
    }

    private loadPracticeAreas(): void {
        this.loading = true;
        this.error = null;

        this.tasksByPracticeAreaService.getPracticeAreas()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (areas: PracticeArea[]) => {
                    this.practiceAreas = areas;
                    this.loading = false;
                },
                error: (error: Error) => {
                    console.error('Error loading practice areas:', error);
                    this.error = 'Unable to load practice areas. Please try again later.';
                    this.loading = false;
                }
            });
    }

    private loadData(practiceAreas?: string[]): void {
        this.loading = true;
        this.tasksByPracticeAreaService.getTasksByPracticeArea(practiceAreas)
            .subscribe(
                (data: any) => {
                    // Process the received data
                    const processedData = this.processDataWithColors(data);
                    this.chartConfig.data = processedData;
                    this.chartConfig.filteredData = [...processedData];
                    this.updateColorScheme(processedData);
                    this.loading = false;
                },
                (error) => {
                    console.error('Error loading data:', error);
                    this.loading = false;
                }
            );
    }

    private processDataWithColors(data: any[]): any[] {
        // Map each item to include its fixed color
        return data.map(item => ({
            ...item,
            _color: this.practiceAreaColors[item.name] || '#808080' // Store original color
        }));
    }

    private updateColorScheme(data: any[]): void {
        // Always use the original assigned colors in the current data order
        const colorDomain = data.map(item => item._color || this.practiceAreaColors[item.name] || '#808080');
        this.chartConfig.scheme = {
            domain: colorDomain
        };
        
        // Update legend options domain
        if (this.chartConfig.legendOptions) {
            this.chartConfig.legendOptions.domain = data.map(item => item.name);
        }
    }

    private filterData(data: any[], selectedAreaIds: number[]): any[] {
        let filteredData;
        
        // If no areas selected, return all data with original colors
        if (!selectedAreaIds || selectedAreaIds.length === 0) {
            // Return original data with their assigned colors
            filteredData = this.chartConfig.data;
        } else {
            // Get selected practice area descriptions
            const selectedAreaDescriptions = this.practiceAreas
                .filter(area => selectedAreaIds.includes(area.id))
                .map(area => area.description);

            // Filter data while preserving colors
            filteredData = this.chartConfig.data.filter(item => 
                selectedAreaDescriptions.includes(item.name)
            );
        }
        
        // Update color scheme for current data
        this.updateColorScheme(filteredData);
        return filteredData;
    }    applyFilters(): void {
        // With the ng-select configuration now using bindValue="description", 
        // the selectedPracticeAreas should contain the practice area names/descriptions directly
        let selectedAreaNames: string[] = [];
        
        if (Array.isArray(this.selectedPracticeAreas)) {
            // Handle empty array case
            if (this.selectedPracticeAreas.length === 0) {
                // No practice areas selected
            } 
            // Handle case where ng-select returns objects with description
            else if (typeof this.selectedPracticeAreas[0] === 'object' && this.selectedPracticeAreas[0] !== null) {
                selectedAreaNames = this.selectedPracticeAreas.map((area: any) => area.description || area);
            } 
            // Handle case where ng-select returns direct names (expected behavior with bindValue="description")
            else {
                selectedAreaNames = [...this.selectedPracticeAreas];
            }
        }
        
        // Clear any legend selections when using the dropdown filter
        this.selectedItems.clear();
        
        // Store the selected practice area IDs for client-side use if needed
        // Note: We're storing names/descriptions here instead of IDs now
        this.chartConfig.selectedPracticeAreas = selectedAreaNames;
        
        // Only call the API if we have valid selectedAreaNames
        if (selectedAreaNames && selectedAreaNames.length > 0) {
            this.loadData(selectedAreaNames);
        } else {
            this.loadData();
        }
        
        // Force chart update after data is loaded (handled in loadData callback)
        setTimeout(() => {
            this.updateChartSize();
        });
    }

    @HostListener('window:resize')
    updateChartSize(): void {
        if (this.chartContainer) {
            const container = this.chartContainer.nativeElement;
            const containerWidth = container.offsetWidth;
            const containerHeight = container.offsetHeight || 450; // Use actual container height or fallback
            
            // Calculate optimal dimensions for the chart
            let width, height;
            
            // Reserve space for legend and labels (approximately 60-80px)
            const reservedSpace = 70;
            const calculatedHeight = Math.max(containerHeight - reservedSpace, 280);
            
            // Responsive sizing based on container dimensions
            if (containerWidth < 500) {
                width = Math.max(containerWidth - 20, 300);
                height = Math.min(calculatedHeight, 400);
            } else if (containerWidth < 800) {
                width = Math.max(containerWidth - 30, 400);
                height = Math.min(calculatedHeight, 420);
            } else {
                width = Math.max(containerWidth - 40, 450);
                height = calculatedHeight;
            }
            
            // Set the view dimensions for the chart
            this.chartConfig.view = [width, height];
        }
    }

    retry(): void {
        this.error = null;
        this.loadPracticeAreas();
        this.loadData();
    }
    
    /**
     * Toggle filter visibility
     * Note: This method is now controlled by the parent component through isFilterVisible
     */
    toggleFilters(): void {
        // This method is now deprecated as filter visibility is controlled by the parent
        // through the isFilterVisible input property
        this.showFilters = true; // Always true as visibility is controlled by isFilterVisible
    }
    
    // Custom legend methods
    getColor(item: ChartData): string {
        const itemName = item.name;
        return this.practiceAreaColors[itemName] || '#ccc'; // Fallback color
    }
    
    isInactive(item: ChartData): boolean {
        // If nothing is selected, everything is active
        if (this.selectedItems.size === 0) {
            return false;
        }
        // Otherwise, only selected items are active
        return !this.selectedItems.has(item.name);
    }
    
    toggleSelection(item: ChartData): void {
        // Toggle the selection state
        if (this.selectedItems.has(item.name)) {
            this.selectedItems.delete(item.name);
        } else {
            this.selectedItems.add(item.name);
        }
        
        // Apply the filter based on the selection
        this.applyLegendFilter();
        
        // Update the chart after the filter is applied
        setTimeout(() => {
            this.updateChartSize();
        }, 0);
    }
    
    applyLegendFilter(): void {
        // Reset any dropdown filters when using legend selection
        this.chartConfig.selectedPracticeAreas = [];
        
        // If nothing is selected, show everything
        if (this.selectedItems.size === 0) {
            this.chartConfig.filteredData = [...this.chartConfig.data];
        } else {
            // Otherwise, show only the selected items
            this.chartConfig.filteredData = this.chartConfig.data.filter(item => 
                this.selectedItems.has(item.name)
            );
        }
        
        // Update color scheme based on filtered data
        this.updateColorScheme(this.chartConfig.filteredData);
    }
    
    /**
     * Check if any filters are currently active
     * @returns true if there are active filters, false otherwise
     */
    hasActiveFilters(): boolean {
        return this.selectedItems.size > 0 || 
               (this.chartConfig.selectedPracticeAreas && 
                this.chartConfig.selectedPracticeAreas.length > 0);
    }
    
    clearFilters(): void {
        // Clear any active filters
        this.chartConfig.selectedPracticeAreas = [];
        this.selectedPracticeAreas = []; // Clear ng-select dropdown
        this.selectedItems.clear(); // Clear legend selections
        
        // Reload data without filters
        this.loadData();
        
        // Update chart size after data is loaded
        setTimeout(() => {
            this.updateChartSize();
        }, 0);
    }
    
    onPracticeAreaSelectionChange(event: any): void {
        // Track practice area selection changes
    }

    onSelect(event: any): void {
        // Track chart item selection
    }

    @HostListener('window:resize')
    onResize(): void {
        // Use debouncing to prevent excessive updates during resize
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        
        this.resizeTimeout = setTimeout(() => {
            this.updateChartSize();
        }, 150); // Wait 150ms after resize ends before updating
    }
    
    private resizeTimeout: any;

    ngOnDestroy(): void {
        // Clean up resize timeout if it exists
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        
        // Clean up observables
        this.destroy$.next();
        this.destroy$.complete();
    }
}
